import React, { useState, useEffect, useMemo, useCallback } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Input, CheckInput } from "../../../../../common/Input";
import { PROCESSOR_ACCESS_SCHEMA } from "../../../../../constants/employeeTransactionAccessSchema";

const getInitialFormState = () => {
  const initialState = { code: "", description: "" };
  Object.values(PROCESSOR_ACCESS_SCHEMA).forEach(tab => {
    tab.permissions.forEach(p => {
      initialState[p.backendProp] = false;
    });
  });
  return initialState;
};

const ProcessorAccessAddEditModal = ({ user, isOpen, onClose, selectedItem, onSave, showGeneralMessage }) => {
  const [form, setForm] = useState(getInitialFormState());
  const [errors, setErrors] = useState({});

  const allPermissions = useMemo(() => {
    return Object.values(PROCESSOR_ACCESS_SCHEMA).flatMap(tab => tab.permissions);
  }, []);

  /**
   * HELPER: Resolves all prerequisites for a given permission, 
   * including those defined dynamically via Filter Tagging.
   */
  const getResolvedPrerequisites = useCallback((permission) => {
    const staticPrereqs = permission.prerequisite || [];
    let dynamicPrereqs = [];

    // Check for dynamic prerequisites from FEATURES filters
    if (permission.prerequisiteFromFilter && Array.isArray(permission.prerequisiteFromFilter)) {
      const activeFilters = permission.prerequisiteFromFilter;

      // Find all permissions in the schema that "tag" themselves with these filters
      const matchingPermissions = allPermissions.filter(p => {
        if (!p.dynamicFilterTagging) return false;

        // Check if any of the tags match the active filters
        return p.dynamicFilterTagging.some(tag =>
          activeFilters.some(filter => tag.includes(filter))
        );
      });

      dynamicPrereqs = matchingPermissions.map(mp => mp.backendProp);
    }

    // Return unique list of all backendProps
    return [...new Set([...staticPrereqs, ...dynamicPrereqs])];
  }, [allPermissions]);

  useEffect(() => {
    if (isOpen) {
      setErrors({});
      if (selectedItem) {
        setForm({ ...getInitialFormState(), ...selectedItem });
      } else {
        setForm(getInitialFormState());
      }
    }
  }, [selectedItem, isOpen]);

  /**
   * Logic to check if a field is locked because it's a prerequisite of a checked item.
   */
  const isPrerequisiteLocked = (backendProp) => {
    return allPermissions.some(p => {
      if (form[p.backendProp] !== true) return false;
      const prereqs = getResolvedPrerequisites(p);
      return prereqs.includes(backendProp);
    });
  };

  /**
   * Handles checkbox changes
   */
  const handlePermissionChange = (prop, value) => {
    setForm((prev) => {
      const updatedForm = { ...prev, [prop]: value };

      if (value === true) {
        const fieldSchema = allPermissions.find(p => p.backendProp === prop);
        if (fieldSchema) {
          const prereqs = getResolvedPrerequisites(fieldSchema);
          prereqs.forEach(preProp => {
            updatedForm[preProp] = true;
          });
        }
      }

      return updatedForm;
    });
  };

  // ... (validate, handleSave, handleFieldChange remain the same) ...
  const validate = () => {
    const newErrors = {};
    if (!form.code?.trim()) newErrors.code = "Access Code is required";
    if (!form.description?.trim()) newErrors.description = "Description is required";
    const hasAtLeastOnePermission = allPermissions.some(p => !!form[p.backendProp]);
    if (!hasAtLeastOnePermission) {
      showGeneralMessage("Please select at least one permission.", false);
      return false;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const auditEntries = [];
    if (selectedItem) {
      allPermissions.forEach(p => {
        const oldVal = selectedItem[p.backendProp] ? "Enabled" : "Disabled";
        const newVal = form[p.backendProp] ? "Enabled" : "Disabled";
        if (oldVal !== newVal) {
          auditEntries.push({
            setupArea: "Processor Access",
            actionTaken: "update",
            field: `${form.code} - ${p.displayLabel}`,
            oldValue: oldVal, newValue: newVal, dateSaved: new Date(), savedById: user.id
          });
        }
      });
    }
    const res = await onSave(form, selectedItem?.id, auditEntries);
    if (res.success) onClose();
  };

  const handleFieldChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) {
      setErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto">
      <div className="bg-white w-full md:w-[700px] rounded-lg shadow-xl flex flex-col max-h-[95vh]">
        <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white rounded-t-lg z-10">
          <h2 className="text-xl font-bold">{selectedItem ? "Edit" : "Create"} Processor Access</h2>
          <button onClick={onClose} className="bg-transparent border-none cursor-pointer"><CloseIcon /></button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg border">
            <Input
              label="Access Code"
              value={form.code || ""}
              placeholder="Enter Access Code"
              onChange={e => handleFieldChange("code", e.target.value.toUpperCase())}
              disabled={!!selectedItem}
              isRequired={true}
              error={!!errors.code}
              errorMessage={errors.code}
            />
            <Input
              label="Description"
              value={form.description || ""}
              placeholder="Enter Description"
              onChange={e => handleFieldChange("description", e.target.value)}
              isRequired={true}
              error={!!errors.description}
              errorMessage={errors.description}
            />
          </div>

          <div className="space-y-6">
            {Object.keys(PROCESSOR_ACCESS_SCHEMA).map(key => (
              <div key={key} className="border rounded-lg overflow-hidden shadow-sm">
                <div className="bg-blue-900 px-4 py-2">
                  <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                    {PROCESSOR_ACCESS_SCHEMA[key].tabLabel}
                  </h3>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                  {PROCESSOR_ACCESS_SCHEMA[key].permissions.map(p => {
                    const isLocked = isPrerequisiteLocked(p.backendProp);
                    return (
                      <div key={p.backendProp} title={isLocked ? "Required by another enabled permission" : ""}>
                        <CheckInput
                          name={p.displayLabel}
                          value={!!form[p.backendProp]}
                          onChange={val => handlePermissionChange(p.backendProp, val)}
                          isEditMode={!isLocked}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-2 p-5 border-t sticky bottom-0 bg-white rounded-b-lg">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded border-none cursor-pointer">Cancel</button>
          <button onClick={handleSave} className="px-8 py-2 bg-blue-600 text-white rounded font-bold border-none cursor-pointer">
            Save Access Template
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProcessorAccessAddEditModal;